import {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {toast} from 'react-toastify'
import {URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {useRealtimeStore} from '../../../store/realtimeStore'
import {useGetUser} from '../../../store/userStore'

export type PolicyChatUser = {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  avatar?: string | null
}

export type PolicyChatAttachment = {
  id: number
  attachment_type: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE'
  mime_type: string
  original_name: string
  size_bytes: number
  download_url: string
  inline_url: string
}

export type PolicyChatParticipant = {
  id: number
  role: 'ADMIN' | 'MEMBER'
  muted: boolean
  left_at?: string | null
  last_read_at?: string | null
  is_self: boolean
  user: PolicyChatUser
}

export type PolicyChatMessageReaction = {
  emoji: string
  count: number
  reacted_by_self: boolean
  user_names: string[]
}

export type PolicyChatMessage = {
  id: number
  thread_id: number
  text: string
  message_type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE' | 'SYSTEM'
  created_at: string
  updated_at: string
  edited_at?: string | null
  deleted_at?: string | null
  metadata?: Record<string, unknown>
  is_own: boolean
  sender?: PolicyChatUser | null
  reply_to?: {
    id: number
    text: string
    message_type: string
    created_at: string
    sender?: PolicyChatUser | null
    attachments?: PolicyChatAttachment[]
  } | null
  attachments: PolicyChatAttachment[]
  reactions: PolicyChatMessageReaction[]
}

export type PolicyChatAssignmentGroup = {
  id: number
  name: string
  organization_id?: number | null
  member_count: number
  supervisor_count: number
  participant_count: number
  participant_preview: string[]
  thread_id?: number | null
  has_thread: boolean
}

export type PolicyChatThread = {
  id: number
  thread_type: 'DIRECT' | 'GROUP'
  title: string
  display_name: string
  assignment_group?: {
    id: number
    name: string
  } | null
  organization_id: number
  participant_count: number
  participants: PolicyChatParticipant[]
  last_message?: {
    id: number
    text: string
    message_type: string
    created_at: string
    sender?: PolicyChatUser | null
    attachments?: PolicyChatAttachment[]
  } | null
  last_activity_at?: string | null
  unread_count: number
  last_read_at?: string | null
  muted: boolean
  mute_until?: string | null
  is_archived: boolean
  is_pinned: boolean
  is_favorite: boolean
  label: string
  marked_unread: boolean
}

export type MuteDuration = 'off' | '1h' | '8h' | '1w' | 'always'

const sortThreads = (threads: PolicyChatThread[]) =>
  [...threads].sort((left, right) => {
    if (left.is_pinned !== right.is_pinned) {
      return left.is_pinned ? -1 : 1
    }
    const leftTime = new Date(left.last_activity_at || 0).getTime()
    const rightTime = new Date(right.last_activity_at || 0).getTime()
    return rightTime - leftTime
  })

const upsertThread = (threads: PolicyChatThread[], thread: PolicyChatThread) => {
  const remaining = threads.filter((candidate) => candidate.id !== thread.id)
  return sortThreads([thread, ...remaining])
}

const upsertMessage = (messages: PolicyChatMessage[], message: PolicyChatMessage) => {
  const remaining = messages.filter((candidate) => candidate.id !== message.id)
  return [...remaining, message].sort(
    (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
  )
}

const getReadableError = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data?.error
    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError
    }
  }
  return fallback
}

export const usePolicyChat = () => {
  const user = useGetUser((state) => state.user)
  const token = useMemo(
    () => user?.token || window.localStorage.getItem(STORAGE_NAMES.TOKEN) || '',
    [user?.token]
  )
  const lastEventType = useRealtimeStore((state) => state.lastEventType)
  const lastEventPayload = useRealtimeStore((state) => state.lastEventPayload)
  const lastEventTimestamp = useRealtimeStore((state) => state.lastEventTimestamp)

  const [threads, setThreads] = useState<PolicyChatThread[]>([])
  const [assignmentGroups, setAssignmentGroups] = useState<PolicyChatAssignmentGroup[]>([])
  const [messages, setMessages] = useState<PolicyChatMessage[]>([])
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)
  const [availableUsers, setAvailableUsers] = useState<PolicyChatUser[]>([])
  const [loadingThreads, setLoadingThreads] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [typingUsers, setTypingUsers] = useState<
    Record<number, Record<number, {fullName: string; expiresAt: number}>>
  >({})

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  )

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) || null,
    [selectedThreadId, threads]
  )

  const fetchThreads = useCallback(async () => {
    if (!token) return
    setLoadingThreads(true)
    try {
      const response = await axios.get<{items: PolicyChatThread[]}>(`${URL}/api/policychat/threads`, {
        headers: authHeaders,
      })
      const nextThreads = sortThreads(response.data.items || [])
      setThreads(nextThreads)
      setSelectedThreadId((current) => {
        if (current && nextThreads.some((thread) => thread.id === current)) {
          return current
        }
        return nextThreads[0]?.id || null
      })
    } catch (error) {
      toast.error(getReadableError(error, 'Unable to load internal chats'))
    } finally {
      setLoadingThreads(false)
    }
  }, [authHeaders, token])

  const fetchUsers = useCallback(
    async (query = '') => {
      if (!token) return
      setLoadingUsers(true)
      try {
        const response = await axios.get<{items: PolicyChatUser[]}>(`${URL}/api/policychat/users`, {
          headers: authHeaders,
          params: query ? {q: query} : undefined,
        })
        setAvailableUsers(response.data.items || [])
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to load chat users'))
      } finally {
        setLoadingUsers(false)
      }
    },
    [authHeaders, token]
  )

  const fetchAssignmentGroups = useCallback(async () => {
    if (!token) return
    try {
      const response = await axios.get<{items: PolicyChatAssignmentGroup[]}>(
        `${URL}/api/policychat/assignment-groups`,
        {
          headers: authHeaders,
        }
      )
      setAssignmentGroups(response.data.items || [])
    } catch (error) {
      toast.error(getReadableError(error, 'Unable to load assignment groups'))
    }
  }, [authHeaders, token])

  const markThreadRead = useCallback(
    async (threadId: number) => {
      if (!token) return
      try {
        await axios.post(
          `${URL}/api/policychat/threads/${threadId}/read`,
          {},
          {
            headers: authHeaders,
          }
        )
        setThreads((current) =>
          current.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  unread_count: 0,
                  last_read_at: new Date().toISOString(),
                }
              : thread
          )
        )
      } catch (error) {
        console.error('Unable to mark policy chat thread as read', error)
      }
    },
    [authHeaders, token]
  )

  const fetchMessages = useCallback(
    async (threadId: number) => {
      if (!token) return
      setLoadingMessages(true)
      try {
        const response = await axios.get<{items: PolicyChatMessage[]; thread?: PolicyChatThread}>(
          `${URL}/api/policychat/threads/${threadId}/messages`,
          {
            headers: authHeaders,
          }
        )
        setMessages(response.data.items || [])
        if (response.data.thread) {
          setThreads((current) => upsertThread(current, response.data.thread as PolicyChatThread))
        }
        await markThreadRead(threadId)
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to load chat messages'))
      } finally {
        setLoadingMessages(false)
      }
    },
    [authHeaders, markThreadRead, token]
  )

  const selectThread = useCallback((thread: PolicyChatThread) => {
    setSelectedThreadId(thread.id)
  }, [])

  const createDirectThread = useCallback(
    async (targetUserId: number) => {
      if (!token) return null
      try {
        const response = await axios.post<{thread: PolicyChatThread}>(
          `${URL}/api/policychat/threads/direct`,
          {user_id: targetUserId},
          {
            headers: authHeaders,
          }
        )
        const nextThread = response.data.thread
        setThreads((current) => upsertThread(current, nextThread))
        setSelectedThreadId(nextThread.id)
        return nextThread
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to open direct chat'))
        return null
      }
    },
    [authHeaders, token]
  )

  const createGroupThread = useCallback(
    async (title: string, participantIds: number[]) => {
      if (!token) return null
      try {
        const response = await axios.post<{thread: PolicyChatThread}>(
          `${URL}/api/policychat/threads/group`,
          {
            title,
            participant_ids: participantIds,
          },
          {
            headers: authHeaders,
          }
        )
        const nextThread = response.data.thread
        setThreads((current) => upsertThread(current, nextThread))
        setSelectedThreadId(nextThread.id)
        return nextThread
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to create the group'))
        return null
      }
    },
    [authHeaders, token]
  )

  const openAssignmentGroupThread = useCallback(
    async (assignmentGroupId: number) => {
      if (!token) return null
      try {
        const response = await axios.post<{thread: PolicyChatThread}>(
          `${URL}/api/policychat/assignment-groups/${assignmentGroupId}/thread`,
          {},
          {
            headers: authHeaders,
          }
        )
        const nextThread = response.data.thread
        setThreads((current) => upsertThread(current, nextThread))
        setSelectedThreadId(nextThread.id)
        return nextThread
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to open the assignment group chat'))
        return null
      }
    },
    [authHeaders, token]
  )

  const performThreadAction = useCallback(
    async (
      threadId: number,
      path: string,
      body: Record<string, unknown> | undefined,
      errorMessage: string,
      method: 'post' | 'delete' = 'post'
    ) => {
      if (!token) return null
      try {
        const config = {headers: authHeaders}
        const response =
          method === 'delete'
            ? await axios.delete<{thread?: PolicyChatThread}>(
                `${URL}/api/policychat/threads/${threadId}/${path}`,
                {...config, data: body || {}}
              )
            : await axios.post<{thread?: PolicyChatThread}>(
                `${URL}/api/policychat/threads/${threadId}/${path}`,
                body || {},
                config
              )
        const nextThread = response.data.thread
        if (nextThread) {
          setThreads((current) => upsertThread(current, nextThread))
        }
        return response.data
      } catch (error) {
        toast.error(getReadableError(error, errorMessage))
        return null
      }
    },
    [authHeaders, token]
  )

  const archiveThread = useCallback(
    (threadId: number, archived: boolean) =>
      performThreadAction(threadId, 'archive', {archived}, 'Unable to archive chat'),
    [performThreadAction]
  )

  const muteThread = useCallback(
    (threadId: number, duration: MuteDuration) =>
      performThreadAction(threadId, 'mute', {duration}, 'Unable to update mute settings'),
    [performThreadAction]
  )

  const pinThread = useCallback(
    (threadId: number, pinned: boolean) =>
      performThreadAction(threadId, 'pin', {pinned}, 'Unable to pin chat'),
    [performThreadAction]
  )

  const labelThread = useCallback(
    (threadId: number, label: string) =>
      performThreadAction(threadId, 'label', {label}, 'Unable to set label'),
    [performThreadAction]
  )

  const markThreadUnread = useCallback(
    (threadId: number) =>
      performThreadAction(threadId, 'unread', undefined, 'Unable to mark as unread'),
    [performThreadAction]
  )

  const favoriteThread = useCallback(
    (threadId: number, favorite: boolean) =>
      performThreadAction(threadId, 'favorite', {favorite}, 'Unable to update favorite'),
    [performThreadAction]
  )

  const clearThread = useCallback(
    (threadId: number) =>
      performThreadAction(threadId, 'clear', undefined, 'Unable to clear chat'),
    [performThreadAction]
  )

  const deleteThread = useCallback(
    async (threadId: number) => {
      if (!token) return null
      try {
        await axios.delete(`${URL}/api/policychat/threads/${threadId}/delete`, {
          headers: authHeaders,
        })
        setThreads((current) => current.filter((thread) => thread.id !== threadId))
        setSelectedThreadId((current) => (current === threadId ? null : current))
        return true
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to delete chat'))
        return null
      }
    },
    [authHeaders, token]
  )

  const notifyTyping = useCallback(
    async (threadId: number, typing: boolean) => {
      if (!token) return
      try {
        await axios.post(
          `${URL}/api/policychat/threads/${threadId}/typing`,
          {typing},
          {headers: authHeaders}
        )
      } catch (error) {
        // Typing is fire-and-forget; ignore failures silently.
      }
    },
    [authHeaders, token]
  )

  const blockUser = useCallback(
    async (targetUserId: number, blocked: boolean) => {
      if (!token) return null
      try {
        if (blocked) {
          await axios.post(
            `${URL}/api/policychat/blocks/${targetUserId}`,
            {blocked: true},
            {headers: authHeaders}
          )
        } else {
          await axios.delete(`${URL}/api/policychat/blocks/${targetUserId}`, {
            headers: authHeaders,
          })
        }
        await fetchThreads()
        return true
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to update block status'))
        return null
      }
    },
    [authHeaders, fetchThreads, token]
  )

  const sendMessage = useCallback(
    async (threadId: number, text: string, files: File[], replyToId?: number | null) => {
      if (!token) return null
      const hasText = Boolean(text.trim())
      const hasFiles = files.length > 0
      if (!hasText && !hasFiles) return null

      const formData = new FormData()
      formData.append('text', text)
      if (replyToId) {
        formData.append('reply_to_id', String(replyToId))
      }
      files.forEach((file) => formData.append('files', file))

      setSendingMessage(true)
      try {
        const response = await axios.post<{message: PolicyChatMessage; thread: PolicyChatThread}>(
          `${URL}/api/policychat/threads/${threadId}/messages/send`,
          formData,
          {
            headers: authHeaders,
          }
        )
        const nextMessage = response.data.message
        const nextThread = response.data.thread
        setMessages((current) => upsertMessage(current, nextMessage))
        setThreads((current) => upsertThread(current, nextThread))
        return nextMessage
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to send the message'))
        return null
      } finally {
        setSendingMessage(false)
      }
    },
    [authHeaders, token]
  )

  const toggleMessageReaction = useCallback(
    async (messageId: number, emoji: string) => {
      if (!token) return null
      try {
        const response = await axios.post<{message: PolicyChatMessage}>(
          `${URL}/api/policychat/messages/${messageId}/reaction`,
          {emoji},
          {
            headers: authHeaders,
          }
        )
        const nextMessage = response.data.message
        setMessages((current) => upsertMessage(current, nextMessage))
        return nextMessage
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to update the reaction'))
        return null
      }
    },
    [authHeaders, token]
  )

  useEffect(() => {
    void fetchThreads()
  }, [fetchThreads])

  useEffect(() => {
    void fetchAssignmentGroups()
  }, [fetchAssignmentGroups])

  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers((current) => {
        const now = Date.now()
        let mutated = false
        const next: typeof current = {}
        Object.entries(current).forEach(([threadKey, userMap]) => {
          const filtered: Record<number, {fullName: string; expiresAt: number}> = {}
          Object.entries(userMap).forEach(([userKey, value]) => {
            if (value.expiresAt > now) {
              filtered[Number(userKey)] = value
            } else {
              mutated = true
            }
          })
          next[Number(threadKey)] = filtered
        })
        return mutated ? next : current
      })
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!selectedThreadId) {
      setMessages([])
      return
    }
    void fetchMessages(selectedThreadId)
  }, [fetchMessages, selectedThreadId])

  useEffect(() => {
    if (!lastEventTimestamp || !lastEventType) return

    if (lastEventType === 'POLICYCHAT_THREAD_UPSERT' && lastEventPayload?.thread) {
      setThreads((current) => upsertThread(current, lastEventPayload.thread as PolicyChatThread))
      return
    }

    if (lastEventType === 'POLICYCHAT_THREAD_READ' && lastEventPayload?.threadId) {
      const threadId = Number(lastEventPayload.threadId)
      setThreads((current) =>
        current.map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                unread_count: Number(lastEventPayload.unreadCount) || 0,
                last_read_at: String(lastEventPayload.lastReadAt || thread.last_read_at || ''),
              }
            : thread
        )
      )
      return
    }

    if (lastEventType === 'POLICYCHAT_MESSAGE_NEW' && lastEventPayload?.message) {
      const incomingMessage = lastEventPayload.message as PolicyChatMessage
      const incomingThreadId = Number(lastEventPayload.threadId || incomingMessage.thread_id)

      if (selectedThreadId === incomingThreadId) {
        setMessages((current) => upsertMessage(current, incomingMessage))
        if (!incomingMessage.is_own) {
          void markThreadRead(incomingThreadId)
        }
      }
      return
    }

    if (lastEventType === 'POLICYCHAT_MESSAGE_UPSERT' && lastEventPayload?.message) {
      const incomingMessage = lastEventPayload.message as PolicyChatMessage
      const incomingThreadId = Number(lastEventPayload.threadId || incomingMessage.thread_id)

      if (selectedThreadId === incomingThreadId) {
        setMessages((current) => upsertMessage(current, incomingMessage))
      }
      return
    }

    if (lastEventType === 'POLICYCHAT_THREAD_DELETE' && lastEventPayload?.threadId) {
      const threadId = Number(lastEventPayload.threadId)
      setThreads((current) => current.filter((thread) => thread.id !== threadId))
      setSelectedThreadId((current) => (current === threadId ? null : current))
      return
    }

    if (
      lastEventType === 'POLICYCHAT_TYPING' &&
      lastEventPayload?.threadId &&
      lastEventPayload?.userId
    ) {
      const threadId = Number(lastEventPayload.threadId)
      const userId = Number(lastEventPayload.userId)
      const fullName = String(lastEventPayload.fullName || 'User')
      const isTyping = Boolean(lastEventPayload.typing)
      setTypingUsers((current) => {
        const threadMap = {...(current[threadId] || {})}
        if (isTyping) {
          threadMap[userId] = {fullName, expiresAt: Date.now() + 6000}
        } else {
          delete threadMap[userId]
        }
        return {...current, [threadId]: threadMap}
      })
      return
    }

    if (
      lastEventType === 'POLICYCHAT_THREAD_PARTICIPANT_READ' &&
      lastEventPayload?.threadId &&
      lastEventPayload?.userId
    ) {
      const threadId = Number(lastEventPayload.threadId)
      const readerUserId = Number(lastEventPayload.userId)
      const lastReadAt = String(lastEventPayload.lastReadAt || '')
      setThreads((current) =>
        current.map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                participants: thread.participants.map((participant) =>
                  participant.user.id === readerUserId
                    ? {...participant, last_read_at: lastReadAt}
                    : participant
                ),
              }
            : thread
        )
      )
    }
  }, [lastEventPayload, lastEventTimestamp, lastEventType, markThreadRead, selectedThreadId])

  return {
    assignmentGroups,
    archiveThread,
    availableUsers,
    blockUser,
    clearThread,
    createDirectThread,
    createGroupThread,
    deleteThread,
    favoriteThread,
    fetchAssignmentGroups,
    fetchThreads,
    fetchUsers,
    labelThread,
    loadingMessages,
    loadingThreads,
    loadingUsers,
    markThreadRead,
    markThreadUnread,
    messages,
    muteThread,
    notifyTyping,
    openAssignmentGroupThread,
    pinThread,
    selectedThread,
    sendMessage,
    sendingMessage,
    selectThread,
    toggleMessageReaction,
    threads,
    token,
    typingUsers,
  }
}
